import {
  BMButton,
  BMDivider,
  BMNumberStepper,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { DropdownItemProps } from "@traveloka/district-bloom/js/components/Inputfield/component/DropdownItem";
import IcPassengerAdult from "@traveloka/icon-kit-web/react/IcPassengerAdult";
import IcPassengerChildKid from "@traveloka/icon-kit-web/react/IcPassengerChildKid";
import IcSystemArrowLeft from "@traveloka/icon-kit-web/react/IcSystemArrowLeft";
import { useState } from "react";
import { Pressable, StyleSheet } from "react-native";
import BMDropdownInput from "../../../../components/DropdownInput";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { Traveler } from "../../../../providers/api/types";
import StickyContainer from "../../Sticky/StickyContainer";
import ChildAgePopup from "./ChildAgePopup";
import { CHILD_DEFAULT_AGE } from "./constants";

type Props = {
  closeNonEmployeePicker: () => void;
  triggerMaxTravelerBanner: () => void;
  numOfAdults: number;
  numOfChildren: number;
  childrenAges: string[];
  handleSaveNonEmployees: (
    numOfAdults: number,
    numOfChildren: number,
    childrenAges: string[]
  ) => void;
  selectedEmployees: Traveler[];
};

const ageDropdownItems = [
  {
    label: "<1",
    value: "<1",
  },
  ...Array.from({ length: 17 }).map((_, index) => ({
    label: (index + 1).toString(),
    value: (index + 1).toString(),
  })),
];

export default function NonEmployeePicker(props: Props) {
  const {
    closeNonEmployeePicker,
    triggerMaxTravelerBanner,
    numOfAdults,
    numOfChildren,
    handleSaveNonEmployees,
    selectedEmployees,
    childrenAges,
  } = props;

  const [localNumOfAdults, setLocalNumOfAdults] = useState(numOfAdults);
  const [localNumOfChildren, setLocalNumOfChildren] = useState(numOfChildren);
  const [localChildrenAges, setLocalChildrenAges] = useState(childrenAges);
  const [isAgePopupVisible, setIsAgePopupVisible] = useState(false);

  const bmTheme = useBMTheme();
  const { CorporateHotelSearchForm, CorporateProductSearchForm } =
    useMobileContentResource();

  const maxAdults = 30 - selectedEmployees.length;
  const hasReachedMaxTravelers =
    selectedEmployees.length + localNumOfAdults + localNumOfChildren >= 30;

  function checkMaxTraveler() {
    if (hasReachedMaxTravelers) {
      triggerMaxTravelerBanner();
      return true;
    }
    return false;
  }

  function handleChildrenChange(value: number) {
    setLocalNumOfChildren((prev) => {
      if (value > prev) {
        if (checkMaxTraveler()) {
          return prev;
        }
        setLocalChildrenAges([...localChildrenAges, ""]);
      } else {
        setLocalChildrenAges(localChildrenAges.slice(0, -1));
      }
      return value;
    });
  }

  function handleAgeChange(item: DropdownItemProps, index: number) {
    setLocalChildrenAges((prev) => {
      const newAges = [...prev];
      newAges[index] = item.value;
      return newAges;
    });
  }

  function nonEmployeeChange(passengerType: "ADULT" | "CHILD", value: number) {
    if (passengerType === "ADULT") {
      setLocalNumOfAdults((prev) => {
        if (value > prev && checkMaxTraveler()) {
          return prev;
        }
        return value;
      });
      return;
    }
    handleChildrenChange(value);
  }

  function handleSubmit() {
    if (localChildrenAges.some((age) => age === "")) {
      setIsAgePopupVisible(true);
      return;
    }
    closeNonEmployeePicker();
    handleSaveNonEmployees(
      localNumOfAdults,
      localNumOfChildren,
      localChildrenAges
    );
  }

  function handleChildrenDefaultAge() {
    const childrenAges = localChildrenAges.map((age) =>
      age === "" ? CHILD_DEFAULT_AGE : age
    );
    closeNonEmployeePicker();
    handleSaveNonEmployees(localNumOfAdults, localNumOfChildren, childrenAges);
  }

  return (
    <StickyContainer
      headerContent={
        <NonEmployeePickerHeader
          closeNonEmployeePicker={closeNonEmployeePicker}
          bmTheme={bmTheme}
        />
      }
      footerContent={
        <NonEmployeePickerFooter onSaveButtonClicked={handleSubmit} />
      }
    >
      <BMView>
        <ChildAgePopup
          isVisible={isAgePopupVisible}
          onCancel={() => setIsAgePopupVisible(false)}
          onContinue={handleChildrenDefaultAge}
        />
        <BMView>
          <BMView style={styles.subTitle}>
            <BMText variant="uiLarge">
              {CorporateHotelSearchForm.numberOfNonEmployee}
            </BMText>
            <BMView>
              <BMView row justify="between" style={styles.nonEmployeeItem}>
                <BMView row align="center" spacing="md">
                  <IcPassengerAdult
                    color={bmTheme.BMComponents.BMStyleIconInkPrimary}
                    width={24}
                    height={24}
                  />
                  <BMText variant="uiLarge">
                    {CorporateProductSearchForm.adultText}
                  </BMText>
                </BMView>
                <BMView style={styles.stepper}>
                  <BMNumberStepper
                    maxValue={maxAdults}
                    value={localNumOfAdults}
                    onChange={(value) => nonEmployeeChange("ADULT", value)}
                  />
                </BMView>
              </BMView>
              <BMDivider type="SUBTLE" />
            </BMView>
            <BMView>
              <BMView row justify="between" style={styles.nonEmployeeItem}>
                <BMView row align="center" spacing="md">
                  <IcPassengerChildKid
                    accentColor={bmTheme.BMComponents.BMStyleIconInkPrimary}
                    color={bmTheme.BMComponents.BMStyleIconInkPrimary}
                    width={24}
                    height={24}
                  />
                  <BMText variant="uiLarge">
                    {CorporateProductSearchForm.childrenText}
                  </BMText>
                </BMView>
                <BMView style={styles.stepper}>
                  <BMNumberStepper
                    maxValue={6}
                    value={localNumOfChildren}
                    onChange={(value) => {
                      nonEmployeeChange("CHILD", value);
                    }}
                  />
                </BMView>
              </BMView>
              <BMView style={styles.ageSection}>
                {new Array(localNumOfChildren).fill(0).map((_, index) => (
                  <BMView
                    style={[styles.ageInput, { zIndex: 6 - index }]}
                    row
                    align="center"
                    key={index}
                  >
                    <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
                      {CorporateProductSearchForm.childrenText} {index + 1}
                    </BMText>
                    <BMView style={styles.ageDropdown}>
                      <BMDropdownInput
                        placeholder={
                          CorporateProductSearchForm.childAgePlaceholder
                        }
                        items={ageDropdownItems}
                        onItemSelect={(item) => handleAgeChange(item, index)}
                        value={localChildrenAges[index]}
                      />
                    </BMView>
                  </BMView>
                ))}
              </BMView>
            </BMView>
          </BMView>
        </BMView>
      </BMView>
    </StickyContainer>
  );
}

type HeaderProps = {
  closeNonEmployeePicker: () => void;
  bmTheme: ReturnType<typeof useBMTheme>;
};

function NonEmployeePickerHeader(props: HeaderProps) {
  const { closeNonEmployeePicker, bmTheme } = props;
  const { CorporateHotelSearchForm } = useMobileContentResource();
  return (
    <BMView>
      <BMView row align="center" spacing="md">
        <Pressable onPress={closeNonEmployeePicker}>
          <IcSystemArrowLeft
            color={bmTheme.BMComponents.BMStyleIconInkPrimary}
            width={24}
            height={24}
          />
        </Pressable>
        <BMText variant="titleSmall" ink="BMStyleTextInkPrimary">
          {CorporateHotelSearchForm.addNonEmployee}
        </BMText>
      </BMView>
      <BMView style={styles.subTitle}>
        <BMText ink="BMStyleTextInkSecondary" variant="uiNormalLi">
          {CorporateHotelSearchForm.nonEmployeeDetails}
        </BMText>
      </BMView>
      <BMView style={styles.divider}>
        <BMDivider type="SUBTLE" />
      </BMView>
    </BMView>
  );
}

type FooterProps = {
  onSaveButtonClicked: () => void;
};

function NonEmployeePickerFooter(props: FooterProps) {
  const { onSaveButtonClicked } = props;
  const { CorporateHotelSearchForm } = useMobileContentResource();

  return (
    <BMView style={styles.footer}>
      <BMButton
        size="LARGE"
        label={CorporateHotelSearchForm.save}
        onPress={() => {
          onSaveButtonClicked();
        }}
        stretchable
      />
    </BMView>
  );
}

const styles = StyleSheet.create({
  subTitle: {
    marginTop: BMPrimitives.BMSize.lg,
  },
  divider: {
    marginTop: BMPrimitives.BMSize.lg,
  },
  nonEmployeeItem: {
    marginVertical: BMPrimitives.BMSize.xl2,
    paddingHorizontal: BMPrimitives.BMSize.xl,
  },
  stepper: {
    width: 120,
  },
  ageSection: {
    marginLeft: BMPrimitives.BMSize.xl4,
    marginRight: BMPrimitives.BMSize.lg,
  },
  ageInput: {
    marginBottom: BMPrimitives.BMSize.xl,
  },
  ageDropdown: {
    marginLeft: "auto",
    width: 120,
  },
  footer: {
    height: 80,
  },
});
